import { Link, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";
import { Suspense, lazy } from "react";
import { DEMO_FIXTURE_CLIENT_SLUG, DEMO_FIXTURE_MONTH } from "./pages/shared/demoFixtures";
import { Shell } from "./pages/shell/Shell";
import { isValidProposalMonth } from "./calendarUiModel";
import { ManagerAccessBoundary } from "./pages/auth";
import { DevQaProvisionPage } from "./pages/dev/DevQaProvisionPage";

const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage").then((module) => ({ default: module.DashboardPage })));
const ClientHomePage = lazy(() => import("./pages/client-home/ClientHomePage").then((module) => ({ default: module.ClientHomePage })));
const WorkspacePage = lazy(() => import("./pages/calendar-workspace/WorkspacePage").then((module) => ({ default: module.WorkspacePage })));
const ClientManagementPage = lazy(() => import("./pages/client-management/ClientManagementPage").then((module) => ({ default: module.ClientManagementPage })));
const AdminClientManagementPage = lazy(() => import("./pages/client-management/ClientManagementPage").then((module) => ({ default: module.AdminClientManagementPage })));
const AdminPage = lazy(() => import("./pages/admin/AdminPage").then((module) => ({ default: module.AdminPage })));
const BriefingPage = lazy(() => import("./pages/briefing/BriefingPage").then((module) => ({ default: module.BriefingPage })));
const ClientReviewLinkPage = lazy(() => import("./pages/client-review/ClientReviewPages").then((module) => ({ default: module.ClientReviewLinkPage })));
const ClientReviewWorkspacePage = lazy(() => import("./pages/client-review/ClientReviewPages").then((module) => ({ default: module.ClientReviewWorkspacePage })));
const DesignPage = lazy(() => import("./pages/design/DesignPage").then((module) => ({ default: module.DesignPage })));
const ClientDefaultsPage = lazy(() => import("./pages/configuration/ConfigurationPages").then((module) => ({ default: module.ClientDefaultsPage })));
const GlobalCalendarPage = lazy(() => import("./pages/configuration/ConfigurationPages").then((module) => ({ default: module.GlobalCalendarPage })));
const MonthlyProposalConfigurationPage = lazy(() => import("./pages/configuration/ConfigurationPages").then((module) => ({ default: module.MonthlyProposalConfigurationPage })));

function RootRoutePage() {
  return (
    <Suspense fallback={<main className="page-grid"><div className="workspace-notice info" role="status">Loading workspace…</div></main>}>
      <Shell />
    </Suspense>
  );
}

function InvalidProposalMonth({ month }: { month: string }) {
  return (
    <section className="page-grid">
      <div className="workspace-notice warning" role="alert">
        <strong>That proposal month is not valid</strong>
        <span>{month} is not a real YYYY-MM month. Choose an available proposal from Clients.</span>
      </div>
      <Link to="/clients">Open Clients</Link>
    </section>
  );
}

function ClientHomeRoutePage() {
  const { clientSlug } = clientRoute.useParams();
  return <ClientHomePage clientSlug={clientSlug} />;
}

function CalendarRoutePage() {
  const { clientSlug, month } = calendarRoute.useParams();
  return <WorkspacePage clientSlug={clientSlug} month={month} />;
}

function BriefingRoutePage() {
  const { clientSlug, month } = briefingRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <BriefingPage clientSlug={clientSlug} month={month} />;
}

function DesignRoutePage() {
  const { clientSlug, month } = designRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <DesignPage clientSlug={clientSlug} month={month} />;
}

function ClientReviewWorkspaceRoutePage() {
  const { clientSlug, month } = clientReviewWorkspaceRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <ClientReviewWorkspacePage clientSlug={clientSlug} month={month} />;
}

function ClientReviewLinkRoutePage() {
  const { reviewToken } = clientReviewRoute.useParams();
  return <ClientReviewLinkPage reviewToken={reviewToken} />;
}

function ClientDefaultsRoutePage() {
  const { clientSlug } = clientDefaultsRoute.useParams();
  return (
    <ManagerAccessBoundary>
      <ClientDefaultsPage clientSlug={clientSlug} />
    </ManagerAccessBoundary>
  );
}

function MonthlyProposalConfigurationRoutePage() {
  const { clientSlug, month } = monthlyProposalConfigurationRoute.useParams();
  if (!isValidProposalMonth(month)) return <InvalidProposalMonth month={month} />;
  return <MonthlyProposalConfigurationPage clientSlug={clientSlug} month={month} />;
}

const rootRoute = createRootRoute({ component: RootRoutePage });
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: DashboardPage,
});
const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug",
  component: ClientHomeRoutePage,
});
const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/company",
  component: () => <ClientHomePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} />,
});
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/calendar",
  component: CalendarRoutePage,
});
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/workspace",
  component: () => <WorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} />,
});
const briefingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/briefing",
  component: BriefingRoutePage,
});
const briefingAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/briefing",
  component: () => <BriefingPage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} />,
});
const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/design",
  component: DesignRoutePage,
});
const designAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/design",
  component: () => <DesignPage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} />,
});
const clientReviewWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/client-review",
  component: ClientReviewWorkspaceRoutePage,
});
const clientReviewWorkspaceAliasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client-review",
  component: () => <ClientReviewWorkspacePage clientSlug={DEMO_FIXTURE_CLIENT_SLUG} month={DEMO_FIXTURE_MONTH} />,
});
const clientReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client-review/$reviewToken",
  component: ClientReviewLinkRoutePage,
});
const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients",
  component: ClientManagementPage,
});
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminPage,
});
const adminClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/clients",
  component: () => (
    <ManagerAccessBoundary>
      <AdminClientManagementPage />
    </ManagerAccessBoundary>
  ),
});
const calendarsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/calendars",
  component: () => (
    <ManagerAccessBoundary>
      <GlobalCalendarPage />
    </ManagerAccessBoundary>
  ),
});
const clientDefaultsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/defaults",
  component: ClientDefaultsRoutePage,
});
const monthlyProposalConfigurationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/client/$clientSlug/$month/proposal-configuration",
  component: MonthlyProposalConfigurationRoutePage,
});
const devQaProvisionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/__qa/provision",
  component: DevQaProvisionPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  clientRoute,
  companyRoute,
  calendarRoute,
  workspaceRoute,
  briefingRoute,
  briefingAliasRoute,
  designRoute,
  designAliasRoute,
  clientReviewWorkspaceRoute,
  clientReviewWorkspaceAliasRoute,
  clientReviewRoute,
  clientsRoute,
  adminRoute,
  adminClientsRoute,
  calendarsRoute,
  clientDefaultsRoute,
  monthlyProposalConfigurationRoute,
  devQaProvisionRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
